import tippy from "tippy.js";

const TOOLTIP_GAP = 8;

/**
 * The tooltip directive wraps the Tippy library. It takes in an expression
 * which is displayed as the contents of the tooltip. The placement of the
 * tooltip relative to the reference element can be customized using the the
 * placement modifier.
 */
export default (el: HTMLElement, { expression, modifiers }, { cleanup }) => {
  const defaultPlacement = "bottom";

  let placement: "top" | "right" | "bottom" | "left" = defaultPlacement;
  if (modifiers.includes("placement")) {
    let placementIndex = modifiers.indexOf("placement");
    placement = modifiers[placementIndex + 1];
    if (
      placement != "top" &&
      placement != "right" &&
      placement != "bottom" &&
      placement != "left"
    ) {
      placement = defaultPlacement;
    }
  }

  let touch: boolean = true;
  if (modifiers.includes("touch")) {
    let touchIndex = modifiers.indexOf("touch");
    touch = modifiers[touchIndex + 1] === "true";
  }

  let html: boolean = false;
  if (modifiers.includes("html")) {
    let htmlIndex = modifiers.indexOf("html");
    html = modifiers[htmlIndex + 1] === "true";
  }

  let dialog: boolean = false;
  if (modifiers.includes("dialog")) {
    let dialogIndex = modifiers.indexOf("dialog");
    dialog = modifiers[dialogIndex + 1] === "true";
  }

  const tippyObj = (el as any)._tippy
    ? (el as any)._tippy
    : tippy(el, {
        content: expression,
        placement: placement,
        touch: touch,
        allowHTML: html,
        arrow: false,
        offset: [0, TOOLTIP_GAP],
        delay: 0,
        animation: false,
        appendTo: (node) => {
          if (dialog) {
            return (node as HTMLElement).closest("dialog") || document.body;
          }
          return document.body;
        },
        popperOptions: {
          strategy: "absolute",
          modifiers: [
            {
              name: "flip",
              options: {
                fallbackPlacements: ["bottom", "right", "left", "top"],
              },
            },
            {
              name: "preventOverflow",
              options: {
                altAxis: true,
                tether: false,
              },
            },
          ],
        },
      });

  if (!expression || expression === "") {
    tippyObj.disable();
  } else {
    tippyObj.enable();
    tippyObj.setContent(expression);
  }

  /**
   * Tippy handles Escape key presses out of the box, but Alpine appears to be
   * conflicting with this event and preventing it from being recognized, which
   * is why we're manually setting up the event handler below.
   *
   * https://github.com/clio/design-system/issues/621
   *
   * @param event - Any KeyboardEvent fired while the tooltip is shown.
   */
  let handleEscapeKeydown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      tippyObj.hide();
    }
  };

  window.addEventListener("keydown", handleEscapeKeydown);

  cleanup(() => {
    window.removeEventListener("keydown", handleEscapeKeydown);
  });
};
